import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, X, ListChecks, Plus, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';

/**
 * Admin modal to configure the PTW safety checklist per permit type.
 *
 * A tenant can customize the checklist items for each permit type; permits then
 * start from the tenant's list. Types left unconfigured use the built-in default.
 *
 * Backend (routes/ptw.py):
 *   GET    /api/permits/templates/{permit_type}   -> effective checklist + `customized`
 *   PUT    /api/permit-checklist-config           { permit_type, items:[{question}] }
 *   DELETE /api/permit-checklist-config/{permit_type}   (reset to default)
 */
const PERMIT_TYPES = [
  'Hot Work',
  'Cold Work',
  'Confined Space',
  'Work at Height',
  'Electrical Work',
  'Excavation',
  'Lifting Operations',
  'Chemical Handling',
  'Isolation Permit',
  'Radiography',
  'General Maintenance',
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PTWChecklistSettings = ({ open, onClose }) => {
  const { showSuccess, showError, confirm } = useAlert();
  const [permitType, setPermitType] = useState(PERMIT_TYPES[0]);
  const [items, setItems] = useState([]);          // [{ question }]
  const [customized, setCustomized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Whether the current user may edit checklist config. Read-only otherwise —
  // writes are also fail-closed on the backend (admin-only).
  const [canManage, setCanManage] = useState(false);
  // QA #114: a reset is only STAGED in the editor until the user clicks Save.
  const [pendingReset, setPendingReset] = useState(false);

  // Deliberately has no dependencies. The effect below re-runs this loader
  // whenever its identity changes, so depending on showError (or any other
  // context value) would refetch mid-edit and overwrite the user's unsaved
  // changes — which is exactly how a removed checklist item used to come back
  // (QA #112). showError is stable — AlertContext memoizes it.
  const loadTemplate = useCallback(async (type) => {
    setLoading(true);
    setPendingReset(false);
    try {
      const res = await axios.get(`${API}/permits/templates/${encodeURIComponent(type)}`, authHeaders());
      const checklist = Array.isArray(res.data?.checklist) ? res.data.checklist : [];
      setItems(checklist.map(c => ({ question: c.question || '' })));
      setCustomized(!!res.data?.customized);
    } catch (e) {
      showError(e?.response?.data?.detail || 'Failed to load checklist.');
      setItems([]);
      setCustomized(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadTemplate(permitType);
  }, [open, permitType, loadTemplate]);

  // Determine edit permission once when the dialog opens (tenant-level).
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/permit-checklist-config`, authHeaders());
        setCanManage(!!res.data?.can_manage);
      } catch (e) {
        setCanManage(false);
      }
    })();
  }, [open]);

  // Any manual edit after staging a reset means the user is customizing
  // again — Save should persist the edited list, not the reset.
  const updateItem = (i, value) => { setPendingReset(false); setItems(prev => prev.map((it, idx) => idx === i ? { ...it, question: value } : it)); };
  // QA #112: confirm before removing a checklist item (still local until Save).
  const removeItem = async (i) => {
    const preview = (items[i]?.question || '').trim();
    const ok = await confirm(
      'Remove checklist item?',
      preview ? `"${preview.slice(0, 120)}${preview.length > 120 ? '…' : ''}" will be removed. The change applies when you click Save.` : 'This item will be removed. The change applies when you click Save.',
      { confirmLabel: 'Remove', cancelLabel: 'Cancel', destructive: true }
    );
    if (!ok) return;
    setPendingReset(false);
    setItems(prev => prev.filter((_, idx) => idx !== i));
  };
  const addItem = () => { setPendingReset(false); setItems(prev => [...prev, { question: '' }]); };
  const move = (i, dir) => {
    setPendingReset(false);
    setItems(prev => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async () => {
    if (!canManage) { showError('You do not have permission to edit checklist configuration.'); return; }
    setSaving(true);
    try {
      if (pendingReset) {
        // QA #114: the reset staged in the editor is applied only now.
        await axios.delete(`${API}/permit-checklist-config/${encodeURIComponent(permitType)}`, authHeaders());
        setPendingReset(false);
        showSuccess(`${permitType} checklist reset to the built-in default.`);
        // Re-read from the server so the editor shows exactly what is now
        // persisted (also clears the customized flag and the reset banner).
        await loadTemplate(permitType);
      } else {
        const cleaned = items.map(it => ({ question: (it.question || '').trim() })).filter(it => it.question);
        if (cleaned.length === 0) { showError('Add at least one checklist item.'); setSaving(false); return; }
        await axios.put(`${API}/permit-checklist-config`, { permit_type: permitType, items: cleaned }, authHeaders());
        setCustomized(true);
        showSuccess(`Saved. New ${permitType} permits will use this checklist.`);
      }
    } catch (e) {
      showError(e?.response?.data?.detail || 'Failed to save checklist.');
    } finally {
      setSaving(false);
    }
  };

  // QA #114: load the built-in default into the editor as a PREVIEW; nothing
  // is applied until the user clicks Save (closing the dialog discards it).
  const resetToDefault = async () => {
    if (!canManage) { showError('You do not have permission to edit checklist configuration.'); return; }
    const ok = await confirm(
      'Reset checklist to default?',
      `The built-in default items for "${permitType}" will be loaded into the editor. The reset is applied only when you click Save — closing without saving keeps your customized checklist.`,
      { confirmLabel: 'Load default', cancelLabel: 'Cancel', destructive: true }
    );
    if (!ok) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/permits/templates/${encodeURIComponent(permitType)}?use_default=true`, authHeaders());
      const checklist = Array.isArray(res.data?.checklist) ? res.data.checklist : [];
      setItems(checklist.map(c => ({ question: c.question || '' })));
      setPendingReset(true);
    } catch (e) {
      showError(e?.response?.data?.detail || 'Failed to load the default checklist.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Safety Checklist Configuration</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Permit Type</label>
            <select
              value={permitType}
              onChange={(e) => setPermitType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {PERMIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {customized ? 'Customized for this tenant.' : 'Using the built-in default (not yet customized).'}
            </p>
          </div>

          {pendingReset && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
              Default items loaded as a preview. Click <strong>Save</strong> to apply the reset — closing without saving keeps your customized checklist.
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Checklist items</label>
              {items.length === 0 && <p className="text-sm text-gray-500">No items. Add one below.</p>}
              {items.map((it, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-xs text-gray-400 w-5 pt-2 text-right">{i + 1}</span>
                  {canManage ? (
                    <>
                      <textarea
                        value={it.question}
                        onChange={(e) => updateItem(i, e.target.value)}
                        rows="2"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        placeholder="Checklist question"
                      />
                      <div className="flex flex-col gap-1 pt-1">
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30" title="Move up"><ArrowUp className="w-4 h-4" /></button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30" title="Move down"><ArrowDown className="w-4 h-4" /></button>
                      </div>
                      <button type="button" onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700 pt-1" title="Remove"><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <p className="flex-1 px-1 py-2 text-sm text-gray-800">{it.question}</p>
                  )}
                </div>
              ))}
              {canManage && (
                <Button type="button" size="sm" variant="outline" onClick={addItem}><Plus className="w-3 h-3 mr-1" />Add item</Button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <div>
            {canManage && (
              <Button type="button" variant="outline" onClick={resetToDefault} disabled={saving || loading || !customized} title="Delete this tenant's override and use the built-in default">
                <RotateCcw className="w-4 h-4 mr-1" />Reset to default
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Close</Button>
            {canManage && (
              <Button type="button" onClick={save} disabled={saving || loading} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PTWChecklistSettings;
